import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import type { ReportProposalsV1 } from "../lib/reportPdf";
import { refuseInView, sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The monthly report, build round B2 (2026-10-09), decision 0515.
 *
 * Every read is a gateway: the reports and their proposals are tables no browser role holds a
 * privilege on, and the PDF is reached through the Worker with a one-time ticket, as a document is
 * (`documentQueries.ts`), so the browser never holds an object key or a reusable address.
 *
 * The client's press on a proposal ("Gör det", "Få ett pris") is `public.act_on_report_proposal`.
 * It makes one request per proposal; a second press answers with the same request, so the mutation
 * reports which one it got rather than assuming it made a new one.
 */

const MONTH_NAMES = [
  "januari", "februari", "mars", "april", "maj", "juni",
  "juli", "augusti", "september", "oktober", "november", "december"
];

/** "oktober 2026" for `2026-10`, as the database's `private.swedish_month` says it. */
export function monthWords(month: string): string {
  const parts = /^(\d{4})-(\d{2})$/.exec(month);
  return parts ? `${MONTH_NAMES[Number(parts[2]) - 1]} ${parts[1]}` : month;
}

export const clientReportsKey = (tenantId: string) => ["portal", "reports", tenantId] as const;
export const clientOpenFaultsKey = (tenantId: string) => ["portal", "open-faults", tenantId] as const;
export const adminClientReportsKey = (tenantId: string) => ["admin", "client-reports", tenantId] as const;

export type ProposalKind = "included" | "priced";

export interface ClientReportProposal {
  id: string;
  title: string;
  why: string;
  kind: ProposalKind;
  request_number: number | null;
  request_status: string | null;
}

export interface ClientReport {
  report_id: string;
  /** `YYYY-MM`. */
  month: string;
  published_at: string;
  byte_size: number;
  proposals: ClientReportProposal[];
}

export interface OpenFault {
  fault_id: string;
  title: string;
  since: string;
}

export function useClientReports(tenantId: string | null): UseQueryResult<ClientReport[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, clientReportsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<ClientReport>("admin_as_client_reports", { p_tenant_id: source.tenantId });
      }
      const { data, error } = await getSupabaseBrowserClient().rpc("client_monthly_reports", {
        p_tenant_id: tenantId as string
      });
      if (error) {
        throw error;
      }
      return (data ?? []) as unknown as ClientReport[];
    }
  });
}

/** "Fel (under åtgärd)": open now, read as Mercator's month read reads it (`private.report_open_faults`). */
export function useClientOpenFaults(tenantId: string | null): UseQueryResult<OpenFault[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, clientOpenFaultsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<OpenFault>("admin_as_client_open_faults", { p_tenant_id: source.tenantId });
      }
      const { data, error } = await getSupabaseBrowserClient().rpc("client_open_faults", {
        p_tenant_id: tenantId as string
      });
      if (error) {
        throw error;
      }
      return (data ?? []) as OpenFault[];
    }
  });
}

export interface ProposalPress {
  tenantId: string;
  proposalId: string;
}

export interface ProposalPressAnswer {
  requestNumber: number;
  created: boolean;
}

/**
 * The press. The report read is refetched before the mutation settles, so the button turns into the
 * request it made in the same render as the "done" sentence (`.claude/rules/a-mutation-is-done-when-its-read-is.md`).
 */
export function useActOnProposal() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ProposalPress): Promise<ProposalPressAnswer> => {
      refuseInView(source);
      const { data, error } = await getSupabaseBrowserClient().rpc("act_on_report_proposal", {
        p_tenant_id: input.tenantId,
        p_proposal_id: input.proposalId
      });
      if (error) {
        throw error;
      }
      const row = ((data ?? []) as Array<{ request_number: number; created: boolean }>)[0];
      if (!row) {
        throw new Error("PORTAL_REPORT_PROPOSAL_NOT_FOUND");
      }
      return { requestNumber: Number(row.request_number), created: row.created };
    },
    onSettled: (_result, _error, input) =>
      queryClient.invalidateQueries({ queryKey: clientReportsKey(input.tenantId) })
  });
}

async function callWorker(path: string, init: RequestInit): Promise<Response> {
  const { data } = await getSupabaseBrowserClient().auth.getSession();
  const token = data.session?.access_token;

  if (!token) {
    throw new Error("PORTAL_UNAUTHENTICATED");
  }

  const response = await fetch(path, {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` }
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
    const failure = new Error(body?.error ?? `PORTAL_REPORT_FAILED_${response.status}`) as Error & { sentence?: string };
    failure.sentence = body?.message;
    throw failure;
  }

  return response;
}

export interface ReportDownload {
  tenantId: string;
  reportId: string;
  month: string;
}

/**
 * Ticket, then bytes, then a save, as a document is fetched. The file is saved, never shown inside
 * the portal: the Worker answers it as an attachment under `Content-Security-Policy: sandbox`.
 */
export function useReportDownload() {
  const source = useReadSource();
  return useMutation({
    mutationFn: async (input: ReportDownload) => {
      refuseInView(source);
      const ticketResponse = await callWorker("/api/files/report-ticket", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenant_id: input.tenantId, report_id: input.reportId })
      });
      const ticket = (await ticketResponse.json()) as { download_path: string };
      const fileResponse = await callWorker(ticket.download_path, { method: "GET" });
      const blob = await fileResponse.blob();

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `PetrovicSystems-manadsrapport-${input.month}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      return input.reportId;
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Petrovic                                                                     */
/* -------------------------------------------------------------------------- */

export interface AdminReportProposal {
  id: string;
  key: string;
  title: string;
  kind: ProposalKind;
  acted_at: string | null;
  request_number: number | null;
}

export interface AdminClientReport {
  report_id: string;
  month: string;
  published_at: string;
  original_filename: string;
  byte_size: number;
  proposals: AdminReportProposal[];
}

export function useAdminClientReports(tenantId: string, enabled = true): UseQueryResult<AdminClientReport[]> {
  return useQuery({
    queryKey: adminClientReportsKey(tenantId),
    enabled,
    queryFn: async () => {
      const { data, error } = await getSupabaseBrowserClient().rpc("admin_client_reports", { p_tenant_id: tenantId });
      if (error) {
        throw error;
      }
      return (data ?? []) as unknown as AdminClientReport[];
    }
  });
}

export interface ReportPublication {
  tenantId: string;
  file: File;
  bytes: ArrayBuffer;
  proposals: ReportProposalsV1;
}

export interface PublishedReport {
  report_id: string;
  month: string;
  replaced: boolean;
  removed: number;
  notified: number;
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Intent, then the bytes. The intent carries the JSON the browser extracted, and the gateway checks it
 * again; the Worker checks the bytes against the digest declared here and publishes in the same step.
 */
export function usePublishReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ReportPublication): Promise<PublishedReport> => {
      const digest = await sha256Hex(input.bytes);
      const intentResponse = await callWorker("/api/files/report-intent", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          tenant_id: input.tenantId,
          original_filename: input.file.name,
          byte_size: input.bytes.byteLength,
          sha256: digest,
          proposals: input.proposals
        })
      });
      const intent = (await intentResponse.json()) as { upload_path: string; upload_permit: string };

      const stored = await callWorker(intent.upload_path, {
        method: "PUT",
        headers: { "content-type": "application/pdf", "x-upload-permit": intent.upload_permit },
        body: input.bytes
      });

      return (await stored.json()) as PublishedReport;
    },
    // Since build round 44 (2026-10-12) a publication by hand also removes the month's draft from
    // Mercator (`private.drop_report_draft`), so the drafts read is refetched with the reports.
    onSettled: (_result, _error, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: adminClientReportsKey(input.tenantId) }),
        queryClient.invalidateQueries({ queryKey: adminClientReportDraftsKey(input.tenantId) })
      ])
  });
}

/* -------------------------------------------------------------------------- */
/* Drafts from Mercator, build round 44 (2026-10-12)                             */
/* -------------------------------------------------------------------------- */

/**
 * Mercator uploads a client's report the moment Mattej approves it there, and it lands here as an
 * unpublished draft. Publishing stays his press in the portal: `public.admin_publish_report_draft`
 * makes the draft the month's published report exactly as the browser upload does (replacing an
 * earlier one, keeping the latest twelve, notifying the client on a month's first report).
 *
 * Both gateways are new in round 44 and the generated types do not hold them until they are
 * regenerated, so the names and arguments are cast at the call. The answers are cast either way.
 */
export const adminClientReportDraftsKey = (tenantId: string) => ["admin", "client-report-drafts", tenantId] as const;

export interface AdminReportDraftProposal {
  id: string;
  key: string;
  title: string;
  why: string;
  kind: ProposalKind;
}

export interface AdminReportDraft {
  report_id: string;
  /** `YYYY-MM`. */
  month: string;
  /** `business_operating_system` from the new upload path, since build round 50 (2026-10-27). */
  source: "mercator" | "business_operating_system";
  drafted_at: string;
  original_filename: string;
  byte_size: number;
  proposals: AdminReportDraftProposal[];
}

export function useAdminClientReportDrafts(tenantId: string, enabled = true): UseQueryResult<AdminReportDraft[]> {
  return useQuery({
    queryKey: adminClientReportDraftsKey(tenantId),
    enabled,
    queryFn: async () => {
      const { data, error } = await getSupabaseBrowserClient().rpc(
        "admin_client_report_drafts" as never,
        { p_tenant_id: tenantId } as never
      );
      if (error) {
        throw error;
      }
      return ((data ?? []) as unknown as AdminReportDraft[]).map((draft) => ({
        ...draft,
        byte_size: Number(draft.byte_size),
        proposals: draft.proposals ?? []
      }));
    }
  });
}

export interface ReportDraftPublication {
  tenantId: string;
  reportId: string;
}

/**
 * Publicera on a draft. A month the client has already acted on answers `refused_acted_on`, which
 * is a failure the screen states with the upload's own sentence. Both reads are refetched before the
 * mutation settles, so the draft leaves the list and the published row appears in the same render as
 * the "done" sentence (`.claude/rules/a-mutation-is-done-when-its-read-is.md`).
 */
export function usePublishReportDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ReportDraftPublication): Promise<PublishedReport> => {
      const { data, error } = await getSupabaseBrowserClient().rpc(
        "admin_publish_report_draft" as never,
        { p_report_id: input.reportId } as never
      );
      if (error) {
        throw error;
      }
      const answer = (data ?? null) as unknown as ({ outcome?: string } & Partial<PublishedReport>) | null;
      if (answer?.outcome === "refused_acted_on") {
        throw new Error("PORTAL_REPORT_ALREADY_ACTED_ON");
      }
      if (answer?.outcome !== "published" || !answer.month) {
        throw new Error("PORTAL_REPORT_DRAFT_UNEXPECTED_ANSWER");
      }
      return {
        report_id: String(answer.report_id ?? input.reportId),
        month: answer.month,
        replaced: Boolean(answer.replaced),
        removed: Number(answer.removed ?? 0),
        notified: Number(answer.notified ?? 0)
      };
    },
    onSettled: (_result, _error, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: adminClientReportDraftsKey(input.tenantId) }),
        queryClient.invalidateQueries({ queryKey: adminClientReportsKey(input.tenantId) })
      ])
  });
}

export interface ReportDraftDiscard {
  tenantId: string;
  reportId: string;
}

/**
 * Kasta utkastet, build round 46b (2026-10-23). `public.admin_discard_report_draft` deletes a draft
 * from Mercator that was never published, with its file, and tells nobody. Only a draft can go: a
 * pending upload and a published report are refused with codes of their own, which
 * `REPORT_DISCARD_REFUSAL_SENTENCE` words. The drafts read is refetched before the mutation settles, so
 * the row leaves in the same render as the "done" sentence.
 */
export function useDiscardReportDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ReportDraftDiscard): Promise<{ month: string }> => {
      const { data, error } = await getSupabaseBrowserClient().rpc("admin_discard_report_draft", {
        p_report_id: input.reportId
      });
      if (error) {
        throw error;
      }
      const answer = (data ?? null) as { outcome?: string; month?: string } | null;
      if (answer?.outcome !== "discarded" || !answer.month) {
        throw new Error("PORTAL_REPORT_DRAFT_UNEXPECTED_ANSWER");
      }
      return { month: answer.month };
    },
    onSettled: (_result, _error, input) =>
      queryClient.invalidateQueries({ queryKey: adminClientReportDraftsKey(input.tenantId) })
  });
}

/** A discard's refusals, each in its own words. An unknown code is said as such, never as "försök igen". */
export const REPORT_DISCARD_REFUSAL_SENTENCE: Record<string, string> = {
  PORTAL_REPORT_DRAFT_NOT_FOUND: "Utkastet finns inte längre. Det kan redan vara publicerat, kastat eller ersatt av en nyare uppladdning.",
  PORTAL_REPORT_DRAFT_STILL_UPLOADING: "Rapporten laddas fortfarande upp från Business Operating System. Den kan kastas när den har blivit ett utkast.",
  PORTAL_REPORT_ALREADY_PUBLISHED: "Rapporten är redan publicerad och kunden kan se den. Ett publicerat utkast kan inte kastas.",
  PORTAL_FORBIDDEN_ADMIN_REQUIRED: "Bara Petrovic, inloggad med andra steget, kan kasta ett utkast. Logga in igen.",
  PORTAL_REPORT_DRAFT_UNEXPECTED_ANSWER: "Portalen svarade på ett sätt skärmen inte känner igen. Ladda om sidan och se om utkastet finns kvar."
};

/** The refusals a publication can meet, in the screen's words. Unknown codes fall back to the sentence the Worker sent. */
export const REPORT_REFUSAL_SENTENCE: Record<string, string> = {
  PORTAL_REPORT_ALREADY_ACTED_ON:
    "Kunden har redan tryckt på ett förslag i den här månadens rapport, så den kan inte bytas. Förslagen pekar på ärendena som skapades.",
  PORTAL_REPORT_DRAFT_NOT_FOUND: "Utkastet finns inte längre. Det kan redan vara publicerat eller ersatt.",
  PORTAL_REPORT_CLIENT_MISMATCH: "Rapporten är skriven för en annan kund.",
  PORTAL_REPORT_JSON_MALFORMED: "Förslagsfilen har inte formatet version 1.",
  PORTAL_REPORT_VERSION_UNSUPPORTED: "Förslagsfilen har en version portalen inte känner till.",
  PORTAL_REPORT_MONTH_MALFORMED: "Månaden i förslagsfilen går inte att läsa, eller har inte börjat än.",
  PORTAL_REPORT_TOO_MANY_PROPOSALS: "Rapporten har fler än tio förslag.",
  PORTAL_REPORT_PROPOSAL_MALFORMED: "Ett förslag saknar något av id, rubrik, motivering eller typ.",
  PORTAL_REPORT_PROPOSAL_ID: "Två förslag har samma id, eller ett id är ogiltigt.",
  PORTAL_REPORT_PROPOSAL_LENGTH: "En rubrik är längre än 120 tecken eller en motivering längre än 600.",
  PORTAL_REPORT_NOT_PLAIN_TEXT: "Ett förslag innehåller tecken som inte är vanlig text.",
  PORTAL_REPORT_PROPOSAL_KIND: "Ett förslag är varken Ingår eller Kostar.",
  PORTAL_UPLOAD_TOO_LARGE: "Filen är större än 15 MB.",
  upload_too_large: "Filen är större än 15 MB.",
  report_not_a_pdf: "Filen är inte en PDF.",
  report_hash_mismatch: "Filen ändrades under uppladdningen. Försök igen."
};
